import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ParentLayout } from "@/components/layout/ParentLayout";
import { Avatar } from "@/components/ui/Avatar";
import { Card } from "@/components/ui/Card";
import { Pill } from "@/components/ui/Pill";
import { useObservable } from "@/hooks/useObservable";
import { formatKr } from "@/lib/format";
import type {
  ChildWeekAggregate,
  WeekRow,
} from "@/services/parentWeek.service";
import { parentWeekService } from "@/services/parentWeek.service";
import type { WeekStatus } from "@/types/domain";

export function ParentWeeks() {
  const state = useObservable(parentWeekService.state$, {
    weeks: [],
    currentWeek: null,
    awaitingCloseCount: 0,
    loading: true,
  });
  const navigate = useNavigate();

  return (
    <ParentLayout>
      <div style={{ maxWidth: 720 }}>
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: 0.7,
              fontWeight: 700,
            }}
          >
            Familien
          </div>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 34,
              fontWeight: 400,
              marginTop: 2,
              letterSpacing: -0.8,
            }}
          >
            Uker
          </div>
        </div>

        {state.loading ? (
          <div style={{ color: "var(--ink-3)", fontSize: 14 }}>Laster…</div>
        ) : state.weeks.length === 0 ? (
          <Card
            style={{ padding: 24, textAlign: "center", color: "var(--ink-3)" }}
          >
            Ingen uker registrert ennå.
          </Card>
        ) : (
          <Card padding={0} style={{ overflow: "hidden" }}>
            {state.weeks.map((row, i) => (
              <WeekRowItem
                key={`${row.year}-${row.isoWeek}`}
                row={row}
                isLast={i === state.weeks.length - 1}
                onClick={() =>
                  navigate(`/parent/weeks/${row.year}-${row.isoWeek}`)
                }
              />
            ))}
          </Card>
        )}
      </div>
    </ParentLayout>
  );
}

function WeekRowItem({
  row,
  isLast,
  onClick,
}: {
  row: WeekRow;
  isLast: boolean;
  onClick: () => void;
}) {
  return (
    <div
      data-testid="week-row"
      data-year-week={`${row.year}-${row.isoWeek}`}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "16px 20px",
        borderBottom: isLast ? "none" : "1px solid var(--line)",
        cursor: "pointer",
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>
          Uke {row.isoWeek}, {row.year}
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
          {row.weekRangeLabel}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 8,
            flexWrap: "wrap",
          }}
        >
          {row.uniformStatus ? (
            <StatusPill status={row.uniformStatus} />
          ) : (
            row.children.map((c) => (
              <PerChildStatus key={c.child.id} aggregate={c} />
            ))
          )}
        </div>
      </div>
      <div style={{ textAlign: "right", marginRight: 8 }}>
        <div
          className="num"
          data-testid="week-row-payout"
          style={{ fontSize: 18, fontWeight: 700, color: "var(--good)" }}
        >
          +{formatKr(row.totalPayout)}
        </div>
        <div
          data-testid="week-row-job-count"
          style={{ fontSize: 12, color: "var(--ink-3)" }}
        >
          {row.jobCount} jobber
        </div>
      </div>
      <ChevronRight size={14} color="var(--ink-3)" />
    </div>
  );
}

function PerChildStatus({ aggregate }: { aggregate: ChildWeekAggregate }) {
  return (
    <div
      data-testid="per-child-status"
      data-child-id={aggregate.child.id}
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <Avatar person={aggregate.child} size={22} />
      <StatusPill status={aggregate.status} />
    </div>
  );
}

function StatusPill({ status }: { status: WeekStatus }) {
  if (status === "open")
    return (
      <Pill tone="warn" size="sm" data-testid="week-status-pill">
        Åpen
      </Pill>
    );
  if (status === "child_confirmed")
    return (
      <Pill tone="coral" size="sm" data-testid="week-status-pill">
        Bekreftet
      </Pill>
    );
  return (
    <Pill tone="forest" size="sm" data-testid="week-status-pill">
      Lukket
    </Pill>
  );
}
